/**
 * Utility functions for object comparison and change detection
 */

/**
 * Checks if two values are deeply equal using JSON.stringify comparison
 * @param value1 - First value to compare
 * @param value2 - Second value to compare
 * @returns true if values are equal, false otherwise
 */
export const isDeepEqual = (value1: any, value2: any): boolean => {
  return JSON.stringify(value1) === JSON.stringify(value2);
};

/**
 * Creates an object containing only the properties that have changed from the original
 * @param currentValues - Current object values
 * @param originalValues - Original object values to compare against
 * @param deep - Whether to perform deep comparison for nested objects (default: false)
 * @returns Object containing only the changed properties
 */
export const getChangedFields = <T extends Record<string, any>>(
  currentValues: T,
  originalValues: T,
  deep = false
): Partial<T> => {
  if (!deep) {
    // Original shallow behavior
    return Object.fromEntries(
      Object.entries(currentValues).filter(([key, value]) => {
        const originalValue = originalValues[key as keyof T];
        return !isDeepEqual(value, originalValue);
      })
    ) as Partial<T>;
  }

  // Deep comparison - recursively compare nested objects
  const result: any = {};

  for (const [key, value] of Object.entries(currentValues)) {
    const originalValue = originalValues[key as keyof T];

    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      originalValue &&
      typeof originalValue === 'object' &&
      !Array.isArray(originalValue)
    ) {
      // Both are objects - recursively compare
      const nestedChanges = getChangedFields(value, originalValue, true);
      if (Object.keys(nestedChanges).length > 0) {
        result[key] = nestedChanges;
      }
    } else if (!isDeepEqual(value, originalValue)) {
      // Primitive values or arrays - direct comparison
      result[key] = value;
    }
  }

  return result as Partial<T>;
};

/**
 * Checks if an object has any changes compared to the original
 * @param currentValues - Current object values
 * @param originalValues - Original object values to compare against
 * @param deep - Whether to perform deep comparison for nested objects (default: false)
 * @returns true if there are any changes, false otherwise
 */
export const hasChanges = <T extends Record<string, any>>(
  currentValues: T,
  originalValues: T,
  deep = false
): boolean => {
  return (
    Object.keys(getChangedFields(currentValues, originalValues, deep)).length >
    0
  );
};

/**
 * Checks if a computed field has changed from its original value
 * @param currentValue - Current computed value
 * @param originalValue - Original value to compare against
 * @returns true if the computed field has changed, false otherwise
 */
export const hasComputedFieldChanges = (
  currentValue: any,
  originalValue: any
): boolean => {
  return !isDeepEqual(currentValue, originalValue);
};
